// Zod schemas for Inbox Composite definitions — the filter tree stored
// in `public.inbox_composites.definition` (ADR 0029).
//
// Keeping this at the persistence boundary means:
//
//   - A client reading a malformed row parses to the empty default via
//     `.catch()` and renders as "no matches" instead of crashing the
//     sidebar.
//   - Adding a new predicate kind is a one-file change here; older
//     clients parsing a row that contains a newer predicate kind will
//     drop that predicate and still render the rest of the filter.
//   - The TypeScript `CompositeDefinition` type is `z.infer<>` of this
//     schema, so the type can't drift from the validator.

import { z } from 'zod'

// ─── Predicates ─────────────────────────────────────────────────────────────

/** Match conversations tagged with `any` of `tagIds`, or with `all` of
 * them (for MVP the UI only exposes `any`; `all` is here because the
 * evaluator is cheap to generalise). */
export const tagPredicateSchema = z.object({
  kind: z.literal('tag'),
  tagIds: z.array(z.string()).default([]),
  mode: z.enum(['any', 'all']).default('any'),
})

/** Restrict to a specific Inbox sub-view. Matches the existing
 * `inboxView` enum Shell.tsx uses. */
export const inboxViewPredicateSchema = z.object({
  kind: z.literal('inboxView'),
  view: z.enum(['ALL', 'WAITING_THEIRS', 'WAITING_MINE', 'UNREAD']),
})

/** Unread-only / read-only toggle. Separate from the inboxView
 * predicate because the two can combine (e.g. "Waiting theirs AND
 * unread"). */
export const unreadPredicateSchema = z.object({
  kind: z.literal('unread'),
  value: z.boolean(),
})

/** Starred / bookmarked conversations. */
export const starredPredicateSchema = z.object({
  kind: z.literal('starred'),
  value: z.boolean(),
})

/** Category match (INBOX / ARCHIVE / INMAIL / SPONSORED / SPAM /
 * SALESNAV). Wraps the existing category column. */
export const categoryPredicateSchema = z.object({
  kind: z.literal('category'),
  category: z.enum(['INBOX', 'ARCHIVE', 'INMAIL', 'SPONSORED', 'SPAM', 'SALESNAV']),
})

export const predicateSchema = z.discriminatedUnion('kind', [
  tagPredicateSchema,
  inboxViewPredicateSchema,
  unreadPredicateSchema,
  starredPredicateSchema,
  categoryPredicateSchema,
])

export type Predicate = z.infer<typeof predicateSchema>

/** Parse a single predicate, returning `null` for unknown shapes. Used
 * at the outer parse level so an unknown-kind predicate (e.g. from a
 * newer client version) gets dropped without nuking the whole filter.
 * Previously the discriminated-union was applied to the whole array,
 * which meant one bad element failed the entire composite. */
function parseOnePredicate(raw: unknown): Predicate | null {
  const res = predicateSchema.safeParse(raw)
  return res.success ? res.data : null
}

// ─── Composite definition ───────────────────────────────────────────────────

export const compositeDefinitionSchema = z.object({
  /** Bumped when the shape changes in a way older clients can't handle.
   * Right now they all can — v1 is the first and only version. */
  version: z.literal(1).default(1),
  /** How the top-level predicates combine. MVP only uses `and`; `or` is
   * supported in the evaluator so the UI can enable it later without a
   * schema bump. */
  combinator: z.enum(['and', 'or']).default('and'),
  // Per-element parse with graceful unknown-kind skip. A future client
  // version might add `{kind: 'personType', ...}`; an older client
  // parsing that row should keep the other predicates intact rather
  // than fall through to `EMPTY_DEFINITION` (which would silently
  // convert a 3-predicate filter into a "match everything" filter).
  predicates: z.array(z.unknown()).default([]).transform((arr) =>
    arr.flatMap((raw) => {
      const p = parseOnePredicate(raw)
      return p ? [p] : []
    }),
  ),
})

export type CompositeDefinition = z.infer<typeof compositeDefinitionSchema>

export const EMPTY_DEFINITION: CompositeDefinition = {
  version: 1,
  combinator: 'and',
  predicates: [],
}

/**
 * Infallible parse — on any shape drift (older or newer client versions
 * writing rows this client can't fully parse) falls back to the empty
 * definition. Caller renders that as "no matches" rather than crashing.
 */
export function parseCompositeDefinition(input: unknown): CompositeDefinition {
  const res = compositeDefinitionSchema.safeParse(input)
  if (res.success) return res.data
  return { ...EMPTY_DEFINITION }
}
